/**
 * Shared react-query plumbing for the Transcripts feature: the query-key
 * factory (the incidentQueryKeys / jobKeys / evidenceKeys pattern) plus the
 * cache policy every Transcripts query shares.
 *
 * Cache policy — transcript text is CJI, so settings are deliberately
 * conservative:
 * - staleTime 0: never serve a transcript without revalidating.
 * - gcTime 0: drop the cache entry the moment no component observes it
 *   (the jobStatusQuery precedent), so transcript text never lingers in
 *   memory behind an unmounted page. This also preserves the exact
 *   pre-migration behavior: the old hand-rolled hooks refetched from scratch
 *   on every mount, and the editor's `initStore(loaded)` effect must not fire
 *   off a stale cached document on remount.
 * - No focus/reconnect refetches: the old hooks fetched only on mount,
 *   id change, or explicit refetch. A surprise background refetch here is not
 *   just a behavior change — the editor re-initializes its draft store from
 *   `loaded`, so an unprompted refetch could clobber in-progress edits.
 * - retry: false — the old hooks issued exactly one request per fetch and
 *   surfaced the (verbatim) error copy immediately; the app-default
 *   shouldRetryQuery would delay it behind retries.
 *
 * Auth-loss hygiene: ProtectedView and SideBar call queryClient.clear() on
 * logout, which wipes these caches with everything else. Moving transcript
 * state out of ad-hoc component state and into the clearable (and gcTime-0)
 * cache is a CJIS-hygiene improvement over the hand-rolled hooks, not a risk.
 */
export const transcriptsKeys = {
  all: ["transcripts"] as const,
  lists: () => [...transcriptsKeys.all, "list"] as const,
  list: (search: string) => [...transcriptsKeys.lists(), { search }] as const,
  details: () => [...transcriptsKeys.all, "detail"] as const,
  detail: (evidenceId: string | undefined) =>
    [...transcriptsKeys.details(), evidenceId] as const,
  versions: (evidenceId: string | undefined) =>
    [...transcriptsKeys.all, "versions", evidenceId] as const,
};

/** CJI-conservative query options shared by every Transcripts query — see the
 *  module doc comment for the rationale behind each setting. */
export const transcriptsCjiCacheOptions = {
  staleTime: 0,
  gcTime: 0,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

/**
 * Error-copy extraction, kept byte-identical to the old hooks' untyped
 * `error.response?.data?.message ?? fallback` lookup so rendered messages
 * don't change.
 */
export const transcriptsErrorMessage = (
  error: unknown,
  fallback: string,
): string =>
  (error as { response?: { data?: { message?: string } } } | null)?.response
    ?.data?.message ?? fallback;
